import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { Button } from 'primeng/button';
import {
  CurrencyComponent,
  FormControlComponent,
  ImageThumbnailComponent,
  PrimengButtonDirective,
  PrimengDividerDirective,
  PrimengTextareaDirective,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { Guid, isNil, Nillable, once } from '@web/utils';
import { AdminOrderDataSourceService } from '../../services';
import {
  AdminOrderRefundData,
  PartialRefundFormGroup,
  RefundEntryFormGroup,
} from '../../models/admin-order-refund-data';
import { Divider } from 'primeng/divider';
import { Textarea } from 'primeng/textarea';

@Component({
  selector: 'app-admin-order-partial-refund-form',
  imports: [
    Button,
    CommonModule,
    CurrencyComponent,
    FormControlComponent,
    ImageThumbnailComponent,
    InputNumber,
    NgOptimizedImage,
    PrimengButtonDirective,
    ReactiveFormsModule,
    SpinnerFilledComponent,
    Divider,
    PrimengDividerDirective,
    Textarea,
    PrimengTextareaDirective,
  ],
  templateUrl: './admin-order-partial-refund-form.component.html',
  styleUrl: './admin-order-partial-refund-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminOrderPartialRefundFormComponent implements OnInit {
  public readonly id = input.required<Guid>();
  public readonly cancelEvent = output();
  public readonly submitEvent = output();

  protected readonly data = signal<Nillable<AdminOrderRefundData>>(null);
  protected readonly form: PartialRefundFormGroup;
  protected readonly commentMaxLength = AdminOrderRefundData.CommentMaxLength;
  protected readonly isNil = isNil;

  private readonly builder = inject(FormBuilder);
  private readonly orderDataSource = inject(AdminOrderDataSourceService);

  public constructor() {
    this.form = this.buildForm();
  }

  public ngOnInit(): void {
    this.fetchData();
  }

  public handleCancelClick(): void {
    this.cancelEvent.emit();
  }

  public handleSubmitClick(): void {
    this.orderDataSource
      .confirmAndPartialRefundOrder({
        orderId: this.id(),
        items: this.toItemRefunds(),
        shipments: this.toShipmentRefunds(),
        comment: this.form.controls.comment.value,
      })
      .pipe(once())
      .subscribe(() => this.submitEvent.emit());
  }

  public hasRefundsToSubmit(): boolean {
    return (
      this.toItemRefunds().length > 0 || this.toShipmentRefunds().length > 0
    );
  }

  protected itemControls(): RefundEntryFormGroup[] {
    return this.form.controls.items.controls;
  }

  protected shipmentControls(): RefundEntryFormGroup[] {
    return this.form.controls.shipments.controls;
  }

  protected itemMaxRefundAmount(index: number): number {
    const item = this.data()?.items[index];

    return item?.refundSummary?.currentPrice ?? item?.price ?? 0;
  }

  protected isItemFullyRefunded(index: number): boolean {
    return this.data()?.items[index]?.refundSummary?.fullyRefunded ?? false;
  }

  protected shipmentMaxRefundAmount(index: number): number {
    const shipment = this.data()?.shipments[index];

    return shipment?.refundSummary?.currentPrice ?? shipment?.price ?? 0;
  }

  protected isShipmentFullyRefunded(index: number): boolean {
    return this.data()?.shipments[index]?.refundSummary?.fullyRefunded ?? false;
  }

  private fetchData(): void {
    this.orderDataSource
      .fetchRefundDataById(this.id())
      .pipe(once())
      .subscribe((data) => {
        this.data.set(data);
        this.fillForm(data);
      });
  }

  private toItemRefunds(): Array<{ id: Guid; refundPrice: string }> {
    const data = this.form.getRawValue();

    return data.items
      .map((refund) => ({
        id: refund.identifier,
        refundPrice: String(refund.amount ?? 0),
      }))
      .filter((refund) => Number(refund.refundPrice) > 0);
  }

  private toShipmentRefunds(): Array<{ id: Guid; refundPrice: string }> {
    const data = this.form.getRawValue();

    return data.shipments
      .map((refund) => ({
        id: refund.identifier,
        refundPrice: String(refund.amount ?? 0),
      }))
      .filter((refund) => Number(refund.refundPrice) > 0);
  }

  private buildForm(): PartialRefundFormGroup {
    return this.builder.group({
      items: this.builder.array<RefundEntryFormGroup>([]),
      shipments: this.builder.array<RefundEntryFormGroup>([]),
      comment: this.builder.control<Nillable<string>>(null, [
        Validators.maxLength(AdminOrderRefundData.CommentMaxLength),
      ]),
    });
  }

  private fillForm(data: AdminOrderRefundData): void {
    this.form.controls.items.clear();
    this.form.controls.shipments.clear();

    data.items.forEach((item) => {
      const control = this.buildRefundEntryFormGroup(
        item.id,
        item.refundSummary?.currentPrice ?? item.price,
      );

      if (item.refundSummary?.fullyRefunded ?? false) {
        control.controls.amount.disable({ emitEvent: false });
      }

      this.form.controls.items.push(control);
    });

    data.shipments.forEach((shipment) => {
      const control = this.buildRefundEntryFormGroup(
        shipment.id,
        shipment.refundSummary?.currentPrice ?? shipment.price,
      );

      if (shipment.refundSummary?.fullyRefunded ?? false) {
        control.controls.amount.disable({ emitEvent: false });
      }

      this.form.controls.shipments.push(control);
    });
  }

  private buildRefundEntryFormGroup(
    identifier: Guid,
    maxAmount: number,
  ): RefundEntryFormGroup {
    return this.builder.group({
      identifier: this.builder.nonNullable.control(identifier),
      amount: this.builder.control<Nillable<number>>(null, [
        Validators.min(0),
        Validators.max(maxAmount),
      ]),
    });
  }
}
